import { useEffect } from 'react';
import { useSlideOutContext } from '../components/SlideOutTab';
import { usePlacesAutocomplete } from './usePlacesAutoComplete';

const TRIGGER_CLASS = 'rwl-open-neighborhood-widget';

const normalizeZipCode = (value: string): string | undefined => {
    const digits = value.replace(/\D/g, '');

    if (digits.length < 5) {
        return undefined;
    }

    if (digits.length === 9) {
        return `${digits.slice(0, 5)}-${digits.slice(5, 9)}`;
    }

    return digits.slice(0, 5);
};

const findZipInForm = (form: HTMLFormElement): string | undefined => {
    const formData = new FormData(form);

    for (const [, rawValue] of formData.entries()) {
        if (typeof rawValue !== 'string') {
            continue;
        }
        const normalized = normalizeZipCode(rawValue);
        if (normalized) {
            return normalized;
        }
    }

    const fallbackInput = form.querySelector<HTMLInputElement>(
        'input[name*="zip" i], input[name*="postal" i]'
    );

    if (fallbackInput?.value) {
        return normalizeZipCode(fallbackInput.value);
    }

    return undefined;
};

const geocodeZip = (
    zip: string,
    setSelectedPlaceId: (placeId: string | undefined) => void,
    setQuery: (query: string) => void
) => {
    setQuery(zip);

    if (
        typeof google === 'undefined' ||
        typeof google.maps === 'undefined' ||
        typeof google.maps.Geocoder === 'undefined'
    ) {
        return;
    }

    const geocoder = new google.maps.Geocoder();

    geocoder.geocode(
        {
            address: zip,
            componentRestrictions: {
                country: 'US',
                postalCode: zip.replace(/\D/g, ''),
            },
        },
        (results, status) => {
            if (
                status === google.maps.GeocoderStatus.OK &&
                results &&
                results.length > 0
            ) {
                const placeId = results[0].place_id;
                if (placeId) {
                    setSelectedPlaceId(placeId);
                }
            }
        }
    );
};

export const useExternalZipFormListener = (enabled?: boolean) => {
    const slideOutControls = useSlideOutContext();
    const { setQuery, setSelectedPlaceId, clearSelectedPlaceId } =
        usePlacesAutocomplete();

    useEffect(() => {
        if (!enabled || !slideOutControls) {
            return;
        }

        const handleSubmit = (event: Event) => {
            const target = event.target;

            if (!(target instanceof HTMLFormElement)) {
                return;
            }

            if (!target.classList.contains(TRIGGER_CLASS)) {
                return;
            }

            const zip = findZipInForm(target);

            if (!zip) {
                return;
            }

            const shouldAllowSubmit = target.dataset.rwlAllowSubmit === 'true';

            if (!shouldAllowSubmit) {
                event.preventDefault();
            }

            slideOutControls.open();
            clearSelectedPlaceId();
            geocodeZip(zip, setSelectedPlaceId, setQuery);
        };

        document.addEventListener('submit', handleSubmit, true);

        return () => {
            document.removeEventListener('submit', handleSubmit, true);
        };
    }, [
        enabled,
        slideOutControls,
        setSelectedPlaceId,
        setQuery,
        clearSelectedPlaceId,
    ]);
};
